
import React, { useEffect, useRef, useCallback } from 'react';

export interface SampleDay {
  day: number;
  title: string;
  morning: string;
  afternoon: string;
  evening: string;
}

export interface SampleVoyage {
  id: string;
  title: string;
  location: string;
  image: string;
  description: string;
  tags: string[];
  days: SampleDay[];
}

interface SampleItineraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  voyage: SampleVoyage | null;
}

const SampleItineraryModal: React.FC<SampleItineraryModalProps> = ({ isOpen, onClose, voyage }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Focus trap: cycle focus within the modal and handle Escape
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const modal = modalRef.current;
    if (!modal) return;
    const focusable = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    closeButtonRef.current?.focus();
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !voyage) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-2xl p-4 animate-fadeIn overflow-y-auto" role="dialog" aria-modal="true" aria-label={`Sample itinerary: ${voyage.title}`}>
      <div ref={modalRef} className="relative w-full max-w-4xl text-charcoal overflow-hidden rounded-2xl flex flex-col max-h-[90vh] liquid-glass-modal">

        {/* Close Button */}
        <button
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Close sample itinerary"
            className="absolute top-6 right-6 z-20 w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white hover:text-charcoal transition-all"
        >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
        </button>

        {/* Hero */}
        <div className="relative h-48 sm:h-64 md:h-80 shrink-0">
             <div
                className="w-full h-full bg-cover bg-center"
                style={{ backgroundImage: `url(${voyage.image})` }}
             ></div>
             <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 to-transparent"></div>
             <div className="absolute bottom-0 left-0 p-8 md:p-12 text-white">
                 <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold mb-2 block">{voyage.location}</span>
                 <h2 className="font-display text-3xl md:text-5xl uppercase tracking-widest leading-none mb-4">{voyage.title}</h2>
                 <div className="flex gap-2">
                    {voyage.tags.map(tag => (
                        <span key={tag} className="px-3 py-1 border border-white/20 text-[10px] uppercase tracking-wider bg-black/20 backdrop-blur-sm">
                            {tag}
                        </span>
                    ))}
                 </div>
             </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 md:p-12 bg-ivory">
            <p className="font-serif text-lg text-gray-600 italic leading-relaxed mb-12 border-l-2 border-gold pl-6">
                "{voyage.description}"
            </p>

            <div className="space-y-16">
                {voyage.days.map((day) => (
                    <article key={day.day}>
                        <header className="flex items-baseline gap-4 border-b border-gray-200 pb-4 mb-8">
                            <span className="font-display text-4xl text-charcoal/40 leading-none">0{day.day}</span>
                            <h3 className="font-display text-xl md:text-2xl uppercase tracking-widest text-charcoal leading-none">{day.title}</h3>
                        </header>

                        <div className="pl-4 md:pl-12 border-l border-gold/30 space-y-12">
                            {/* Morning */}
                            <div className="relative pl-8">
                                <div className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full border border-charcoal bg-ivory"></div>
                                <div className="mb-2">
                                    <h4 className="font-display text-sm uppercase tracking-widest text-gold">Morning</h4>
                                </div>
                                <p className="font-serif text-base text-gray-600 leading-relaxed">{day.morning}</p>
                            </div>

                            {/* Afternoon */}
                            <div className="relative pl-8">
                                <div className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full border border-charcoal bg-ivory"></div>
                                <div className="mb-2">
                                    <h4 className="font-display text-sm uppercase tracking-widest text-gold">Afternoon</h4>
                                </div>
                                <p className="font-serif text-base text-gray-600 leading-relaxed">{day.afternoon}</p>
                            </div>

                            {/* Evening */}
                            <div className="relative pl-8">
                                <div className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full border border-charcoal bg-ivory"></div>
                                <div className="mb-2">
                                    <h4 className="font-display text-sm uppercase tracking-widest text-gold">Evening</h4>
                                </div>
                                <p className="font-serif text-base text-gray-600 leading-relaxed">{day.evening}</p>
                            </div>
                        </div>
                    </article>
                ))}
            </div>

            <div className="mt-16 pt-8 border-t border-gray-200 text-center">
                 <button onClick={onClose} className="px-8 py-3 bg-charcoal text-white font-sans text-xs uppercase tracking-widest hover:bg-gold hover:text-charcoal transition-colors">
                     Close Sample Itinerary
                 </button>
            </div>
        </div>

      </div>
    </div>
  );
};

export default SampleItineraryModal;
